import Timeline, {
  CursorMarker,
  CustomMarker,
  TimelineGroupBase,
  TimelineItemBase
} from "react-calendar-timeline";
import { generatePath, useNavigate } from "react-router-dom";

import { DateTime } from "@bps/date-time";
import {
  mergeStyles,
  Text,
  Theme,
  TooltipHost,
  useTheme
} from "@bps/fluent-ui";
import {
  DeploymentDetail,
  DeploymentRingDetail,
  RolloutDetail
} from "@libs/api/gateways/field/field-ops-gateway.dtos";

import { DeploymentDetailTimelineAnnotation } from "./DeploymentDetailTimelineAnnotation";
import { DeploymentDetailTimelineBox } from "./DeploymentDetailTimelineBox";
import { DeploymentDetailTimelineTooltip } from "./DeploymentDetailTimelineTooltip";

const MAX_DAYS_EITHER_SIDE = 30;
const DAYS_PADDING = 3;

type TimelineItem = TimelineItemBase<DateTime> & {
  isSmallBar?: boolean;
  name: string;
  rollout: RolloutDetail;
  deploymentRing: DeploymentRingDetail;
};

interface DeploymentDetailTimelineProps {
  detail: DeploymentDetail;
  fitZoom: boolean;
}

export const DeploymentDetailTimeline: React.FC<DeploymentDetailTimelineProps> = ({
  detail,
  fitZoom
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  if (detail.deploymentRings.length === 0) {
    return <Text variant="small">No deployment data available.</Text>;
  }

  const groups: TimelineGroupBase[] = detail.deploymentRings.map(
    (deploymentRing, index) => {
      return {
        id: index,
        title: deploymentRing.deploymentRingDisplayName
      };
    }
  );

  const nowMarkerDate = DateTime.now().setZone("UTC");
  const items: TimelineItem[] = getSeriesItemsFromDeploymentDetail(
    detail,
    theme
  );

  const sortDates = (dateTimes: DateTime[]) => {
    return dateTimes.sort((a, b) => {
      const aVal = a?.valueOf() ?? 0;
      const bVal = b?.valueOf() ?? 0;
      if (aVal === bVal) return 0;

      return aVal < bVal ? -1 : 1;
    });
  };

  const minimumSeriesDate = sortDates([
    ...items.map(x => x?.start_time ?? nowMarkerDate),
    nowMarkerDate
  ])[0].minus({ days: DAYS_PADDING });

  const maximumSeriesDate = sortDates([
    ...items.map(x => x?.end_time ?? nowMarkerDate),
    nowMarkerDate
  ])
    .reverse()[0]
    .plus({ days: DAYS_PADDING });

  const beforeLimit = nowMarkerDate.minus({ days: MAX_DAYS_EITHER_SIDE });
  const afterLimit = nowMarkerDate.plus({ days: MAX_DAYS_EITHER_SIDE });

  const earliestDate =
    minimumSeriesDate < beforeLimit && !fitZoom
      ? beforeLimit
      : minimumSeriesDate;

  const latestDate =
    maximumSeriesDate > afterLimit && !fitZoom ? afterLimit : maximumSeriesDate;

  const handleClickItem = (deploymentRingId: string, rolloutId: string) => {
    const to = generatePath(
      "/system/plt/deployment-rings/:deploymentRingId/rollouts/:rolloutId",
      {
        deploymentRingId,
        rolloutId
      }
    );
    navigate(to);
  };

  return (
    <Timeline
      groups={groups}
      items={items}
      className={mergeStyles({
        // By default the primary header and sidebar header have a background color of red.
        // Note that the docs recommend supplying your own TimelineHeader elements, but when we tried it, the headers did not switch to the appropriate higher
        // or lower unit of time upon clicking (i.e. the top Year header should become Month, and the second Month header should become Day).
        // This proved to be difficult to solve so we extrajudiciously control the style of these elements here.
        "& > .rct-header-root": {
          "> .rct-calendar-header": {
            background: `${theme.palette.neutralSecondary} !important`
          },
          "> div[data-testid='sidebarHeader']": {
            background: `${theme.palette.neutralSecondary} !important`
          }
        }
      })}
      defaultTimeStart={earliestDate.valueOf()}
      defaultTimeEnd={latestDate.valueOf()}
      lineHeight={60}
      itemHeightRatio={1}
      onItemSelect={id => {
        const seriesItem = items.find(x => x.id === id)!;
        handleClickItem(
          seriesItem.deploymentRing.deploymentRingId,
          seriesItem.rollout.rolloutId
        );
      }}
      itemRenderer={({ item, getItemProps }) => {
        const props = getItemProps(item.itemProps ?? {});
        const isSmallBar = item.isSmallBar ?? false;

        const { rollout, deploymentRing, title } = item;

        const sortedPackages = rollout.rolloutPackages.sort((a, b) =>
          a.softwarePackageName.localeCompare(b.softwarePackageName)
        );

        const renderTooltip = (
          <DeploymentDetailTimelineTooltip
            key={item.id}
            deploymentRingName={deploymentRing.deploymentRingDisplayName}
            packages={sortedPackages}
            rolloutName={rollout.displayName}
            assignmentSource={deploymentRing.assignmentSource}
            initialDownloadStartUtc={
              rollout?.initialDownloadStartUtc ?? nowMarkerDate
            }
            initialDownloadEndUtc={
              rollout?.initialDownloadEndUtc ?? nowMarkerDate
            }
            initialInstallStartUtc={
              rollout?.initialInstallStartUtc ?? nowMarkerDate
            }
            initialInstallEndUtc={
              rollout?.initialInstallEndUtc ?? nowMarkerDate
            }
            ratio={detail.ratio}
          />
        );

        const id = `rollout-${item.id}`;

        if (title === rollout.displayName) {
          return (
            <DeploymentDetailTimelineBox
              id={id}
              isSmallBar={isSmallBar}
              text={item.name}
              props={props}
              ratio={detail.ratio}
            />
          );
        }

        return (
          <TooltipHost
            content={renderTooltip}
            delay={0}
            calloutProps={{ target: `#${id}` }}
          >
            <DeploymentDetailTimelineBox
              id={id}
              isSmallBar={isSmallBar}
              text={item.name}
              props={props}
              ratio={detail.ratio}
            />
          </TooltipHost>
        );
      }}
    >
      <CursorMarker>{({ styles }) => <div style={styles} />}</CursorMarker>
      <CustomMarker date={nowMarkerDate.valueOf()}>
        {({ styles }) => (
          <DeploymentDetailTimelineAnnotation styles={styles} text="Now" />
        )}
      </CustomMarker>
    </Timeline>
  );
};

function getSeriesItemsFromDeploymentDetail(
  detail: DeploymentDetail,
  theme: Theme
) {
  const nowMarkerDate = DateTime.now().setZone("UTC");
  let rolloutIndex = 0;
  const items: TimelineItem[] = detail.deploymentRings
    .map((deploymentRing, index) => {
      return deploymentRing.rollouts.map(rollout => {
        const rolloutSeries = [
          {
            id: rolloutIndex + 1,
            group: index,
            name: "Download",
            // eslint-disable-next-line camelcase
            start_time: rollout?.initialDownloadStartUtc ?? nowMarkerDate,
            // eslint-disable-next-line camelcase
            end_time: rollout?.initialDownloadEndUtc ?? nowMarkerDate,
            itemProps: {
              style: {
                backgroundColor: theme.palette.blue,
                borderColor: theme.palette.blueDark
              }
            },
            isSmallBar: false,
            rollout,
            deploymentRing
          },
          {
            id: rolloutIndex + 2,
            group: index,
            name: "Install",
            // eslint-disable-next-line camelcase
            start_time: rollout?.initialInstallStartUtc ?? nowMarkerDate,
            // eslint-disable-next-line camelcase
            end_time: rollout?.initialInstallEndUtc ?? nowMarkerDate,
            itemProps: {
              style: {
                backgroundColor: theme.palette.green,
                borderColor: theme.palette.greenDark
              }
            },
            isSmallBar: false,
            rollout,
            deploymentRing
          },
          {
            id: rolloutIndex + 3,
            group: index,
            name: rollout.displayName,
            // eslint-disable-next-line camelcase
            start_time: rollout?.initialDownloadStartUtc ?? nowMarkerDate,
            // eslint-disable-next-line camelcase
            end_time: rollout?.initialInstallEndUtc ?? nowMarkerDate,
            itemProps: {
              style: {
                backgroundColor: theme.palette.red,
                borderColor: theme.palette.redDark
              }
            },
            isSmallBar: true,
            rollout,
            deploymentRing
          }
        ];

        rolloutIndex += 3;
        return rolloutSeries;
      });
    })
    .flat(2);

  return items;
}
